import { isPartner } from '../themes/partnerTheme';

/**
 * Second-factor delivery method stored as `otpType` on the user profile and echoed by `/auth/credential`.
 * Backend must use the same numeric codes.
 */
export const OTP_TYPE = {
  /** SMS one-time code */
  SMS: 1,
  /** Email one-time code */
  EMAIL: 2,
  /** TOTP from Microsoft Authenticator */
  AUTHENTICATOR: 3,
  /** TOTP from Google Authenticator. Same setup endpoints as Microsoft. */
  GOOGLE_AUTHENTICATOR: 4,
};

export const MICROSOFT_AUTHENTICATOR_LABEL = 'Microsoft Authenticator';
export const GOOGLE_AUTHENTICATOR_LABEL = 'Google Authenticator';

/** User-visible copy shared across MFA flows */
export const MFA_COPY = {
  enrollmentVerifyFailed:
    'Invalid code. Please ensure you are entering the code from the most recent entry in your authenticator app.',
  profileSaveNeedsAuthenticatorEnrollment:
    'Use the setup button below and finish enrollment before saving.',
  profileSaveCannotDropAuthenticatorWithoutRemove:
    'Use "Remove app" to turn off your authenticator (password and code required). Saving only your profile cannot disable it.',
};

// ---------------------------------------------------------------------------
// Partner build (e.g. Aviva): SMS-based features off for one codebase, many brands
// ---------------------------------------------------------------------------

function isAvivaPartnerBuild() {
  return isPartner('aviva');
}

/** SMS as a 2FA option in Admin → Settings (and related UX). False on Aviva. */
export function isSmsSecondFactorOptionAvailable() {
  return !isAvivaPartnerBuild();
}

/** Map stored SMS preference to email when SMS is not offered on this build. */
export function normalizeOtpTypeForPartnerUi(otpType) {
  if (!isSmsSecondFactorOptionAvailable() && otpType === OTP_TYPE.SMS) return OTP_TYPE.EMAIL;
  return otpType;
}

// ---------------------------------------------------------------------------
// Profile / settings UI helpers (numeric otpType from API)
// ---------------------------------------------------------------------------

export function isAuthenticatorOtpType(otpType) {
  return otpType === OTP_TYPE.AUTHENTICATOR || otpType === OTP_TYPE.GOOGLE_AUTHENTICATOR;
}

/** Microsoft Authenticator is type 3. Any other authenticator type is Google. */
export function getAuthenticatorAppLabel(otpType) {
  if (otpType === OTP_TYPE.GOOGLE_AUTHENTICATOR) return GOOGLE_AUTHENTICATOR_LABEL;
  return MICROSOFT_AUTHENTICATOR_LABEL;
}

/** User chose an authenticator that is not the one already saved on the profile. */
export function isAuthenticatorEnrollmentPending(formOtpType, profileOtpType) {
  return isAuthenticatorOtpType(formOtpType) && formOtpType !== profileOtpType;
}

export function getOtpTypeLabel(otpType) {
  if (otpType === OTP_TYPE.SMS) return 'SMS';
  if (isAuthenticatorOtpType(otpType)) return getAuthenticatorAppLabel(otpType);
  return 'Email';
}
